import PropTypes from 'prop-types'

import {
  DesktopTable,
  HeaderRow,
  RowGroups,
  RowGroup,
  ValueRow,
  BalanceGutter,
  ValueColumnHeading,
  LabelCell,
  SectionLabel,
  StockName,
  ValueCell,
  Sign,
  UnableToSell,
  ChangeText,
  MobileCards,
  Card,
  CardChangeText,
  CardValues,
  CardValue,
  Arrow,
} from './style'

// Renders a value cell's contents: a dollar amount with a tinted "$", or the
// literal "Unable to sell" (restricted stock) rendered blue as a whole.
const CellContents = ({ value, planned }) => {
  if (value === 'Unable to sell') {
    return <UnableToSell>{value}</UnableToSell>
  }
  if (typeof value === 'string' && value.startsWith('$')) {
    return (
      <>
        <Sign planned={planned}>$</Sign>
        {value.slice(1)}
      </>
    )
  }
  return value
}

// A right-pointing arrow (current → recommended) used between the mobile card's
// two value columns.
const RightArrow = () => (
  <Arrow aria-hidden='true'>
    <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
      <path
        d='M4 10h11M11 6l4 4-4 4'
        stroke='currentColor'
        strokeWidth='1.6'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  </Arrow>
)

const rightLabel = isModified => (isModified ? 'Your changes' : 'Recommended')

/**
 * Current-vs-recommended company-stock data table. Unrestricted rows are shown
 * first, then restricted. Desktop is a 3-column grid (labels / CURRENT cell /
 * plan cell); mobile is a stack of bordered cards with a current → recommended
 * arrow. The presentation is chosen by the caller via `isMobile`.
 */
const CompanyStockTable = ({ unrestricted, restricted, isModified, isMobile }) => {
  const rows = [...unrestricted, ...restricted]

  if (isMobile) {
    return (
      <MobileCards>
        {rows.map(row => (
          <Card key={row.id}>
            <StockName>{row.name}</StockName>
            <SectionLabel>{row.label}</SectionLabel>
            {row.changeText && <CardChangeText>{row.changeText}</CardChangeText>}
            <CardValues>
              <CardValue>
                <CellContents value={row.current} />
              </CardValue>
              <RightArrow />
              <CardValue planned>
                <CellContents value={row.recommended} planned />
              </CardValue>
            </CardValues>
          </Card>
        ))}
      </MobileCards>
    )
  }

  return (
    <DesktopTable>
      <HeaderRow>
        <BalanceGutter />
        <ValueColumnHeading>Current</ValueColumnHeading>
        <ValueColumnHeading planned>{rightLabel(isModified)}</ValueColumnHeading>
        <BalanceGutter />
      </HeaderRow>

      <RowGroups>
        {rows.map(row => (
          <RowGroup key={row.id}>
            <ValueRow>
              <LabelCell>
                <SectionLabel>{row.label}</SectionLabel>
                <StockName>{row.name}</StockName>
              </LabelCell>
              <ValueCell>
                <CellContents value={row.current} />
              </ValueCell>
              <ValueCell planned>
                <CellContents value={row.recommended} planned />
              </ValueCell>
              <BalanceGutter />
            </ValueRow>
            {row.changeText && <ChangeText>{row.changeText}</ChangeText>}
          </RowGroup>
        ))}
      </RowGroups>
    </DesktopTable>
  )
}

const rowShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  name: PropTypes.string,
  label: PropTypes.oneOf(['UNRESTRICTED', 'RESTRICTED']),
  current: PropTypes.string,
  recommended: PropTypes.string,
  changeText: PropTypes.string,
})

CompanyStockTable.propTypes = {
  unrestricted: PropTypes.arrayOf(rowShape).isRequired,
  restricted: PropTypes.arrayOf(rowShape).isRequired,
  isModified: PropTypes.bool,
  isMobile: PropTypes.bool,
}

export default CompanyStockTable
